import { CompositeFilterDescriptor, DataResult, filterBy, FilterDescriptor, orderBy, State } from '@progress/kendo-data-query';
import Coordinate from '../common/coordinate';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Airports JSON is so big that it slows down compilation significantly. Try ts-go in the future.
import { FilterGroup } from '@fe/core';
import _staticAirports from './airports.json';

export interface StaticAirport {
    faa?: string;
    icao?: string;
    iata?: string;
    name?: string;
    lat?: number;
    lng?: number;
    runwayLength?: number;
    country?: string; // country or american territory
}

export interface AirportWithDistance extends StaticAirport {
    distance: number;
    bearing: number;
}

const staticAirports = _staticAirports as {
    airports: StaticAirport[];
    faaMapping: Record<string, number>;
    icaoMapping: Record<string, number>;
    iataMapping: Record<string, number>;
};

function findNearestAirports(lat: number, lng: number, state: State): DataResult {
    const take = state.take || 10;
    const skip = state.skip || 0;
    const filters = state.filter;
    const sortDescriptors = state.sort || [];

    // To optimize performance, we apply filters in multiple steps
    // 1. Filter out airports without valid coordinates
    let airports = staticAirports.airports.filter((airport) => {
        return airport.lat != null && airport.lng != null;
    });

    // 2. Apply non-calculated field filters
    const nonCalculatedFilters = extractFilters(filters, ['distance', 'bearing'], false);
    if (nonCalculatedFilters) {
        airports = filterBy(airports, nonCalculatedFilters);
    }

    const origin = new Coordinate(lat, lng);

    const airportsWithDistance: AirportWithDistance[] = airports.map((airport) => {
        const destination = new Coordinate(airport.lat!, airport.lng!);
        const distance = origin.distanceTo(destination.lat, destination.lng) ?? 0;
        const bearing = origin.headingTo(destination) ?? 0;

        return {
            ...airport,
            distance,
            bearing
        };
    });

    // 3. Apply calculated field filters
    const calculatedFilters = extractFilters(filters, ['distance', 'bearing'], true);
    let finalAirports = airportsWithDistance;
    if (calculatedFilters) {
        finalAirports = filterBy(finalAirports, calculatedFilters);
    }

    if (sortDescriptors.length > 0) {
        finalAirports = orderBy(finalAirports, sortDescriptors);
    } else {
        finalAirports.sort((a, b) => a.distance - b.distance);
    }

    const total = finalAirports.length;
    const data = finalAirports.slice(skip, skip + take);

    return {
        data,
        total
    };
}

function extractFilters(
    filter: FilterDescriptor | CompositeFilterDescriptor | undefined,
    calculatedFields: string[],
    includeCalculated: boolean
): FilterDescriptor | CompositeFilterDescriptor | undefined {
    if (!filter) {
        return undefined;
    }

    if (isCompositeFilterDescriptor(filter)) {
        const filters = filter.filters.map((f) => extractFilters(f, calculatedFields, includeCalculated)).filter(Boolean) as Array<
            FilterDescriptor | CompositeFilterDescriptor
        >;

        if (filters.length === 0) {
            return undefined;
        }

        return {
            ...filter,
            filters
        };
    } else {
        const fd = filter as FilterDescriptor;
        const field = typeof fd.field === 'string' ? fd.field : '';
        const isCalculatedField = calculatedFields.includes(field);

        if ((includeCalculated && isCalculatedField) || (!includeCalculated && !isCalculatedField)) {
            return fd;
        }
        return undefined;
    }
}

function isCompositeFilterDescriptor(source: any): source is CompositeFilterDescriptor {
    return source && source.filters;
}

function getByFaaOrIcao(faaOrIcao: string): StaticAirport | null {
    const faaIdx = staticAirports.faaMapping[faaOrIcao];
    if (faaIdx != null) {
        return staticAirports.airports[faaIdx];
    }

    const icaoIdx = staticAirports.icaoMapping[faaOrIcao];
    if (icaoIdx != null) {
        return staticAirports.airports[icaoIdx];
    }

    return null;
}

function getByIcaoOrIata(iataOrIcao: string): StaticAirport | null {
    if (iataOrIcao.length == 3) {
        return staticAirports.airports[staticAirports.iataMapping[iataOrIcao]] ?? null;
    }

    return staticAirports.airports[staticAirports.icaoMapping[iataOrIcao]] ?? null;
}

function getByIata(iata: string): StaticAirport | undefined {
    return staticAirports.airports[staticAirports.iataMapping[iata]];
}

function getByIcao(icao: string): StaticAirport | undefined {
    return staticAirports.airports[staticAirports.icaoMapping[icao]];
}

function getByAnyId(id: string): StaticAirport | undefined {
    return getByIcaoOrIata(id) ?? staticAirports.airports[staticAirports.faaMapping[id]];
}

function getByAnyIds(ids: string[]): Array<StaticAirport | undefined> {
    return ids.map((x) => getByAnyId(x));
}

export function addAlternativeAirportToFilterGroups(groups: FilterGroup[]): FilterGroup[] {
    return groups.map((g) => ({
        ...g,
        members: g.members
            .map((x) => {
                const airport = getByAnyId(x.text.toUpperCase());
                if (airport) {
                    return [airport.icao, airport.iata, airport.faa];
                }

                return [x.text];
            })
            .flat()
            .map((x) => ({ text: x! }))
    }));
}

export function airportIs(airport: StaticAirport, id: string) {
    return airport.icao === id || airport.iata === id || airport.faa === id;
}

export default {
    getByFaaOrIcao,
    getByIcaoOrIata,
    getByIata,
    getByIcao,
    getByAnyId,
    getByAnyIds,
    findNearestAirports,
    staticAirports
};
